/**
 * Bascule vers une nouvelle version de l'interface (révision du référent du 2026-10-31) :
 * logique pure, testée (apps/web/tests/unit/miseajour.test.ts). Une page ouverte prend la
 * nouvelle version d'elle-même, sans jamais perdre une saisie : rechargement immédiat quand
 * rien n'est en cours, sinon bandeau « Nouvelle version disponible » et rechargement complet à
 * la navigation suivante.
 */

export type Bascule = 'recharger' | 'proposer' | 'attendre';

export interface EtatPage {
  /** `navigator.onLine` : hors connexion, on attend le retour du réseau. */
  enLigne: boolean;
  /** Onglet affiché (`document.visibilityState === 'visible'`). */
  visible: boolean;
  /** Un champ modifié par l'utilisateur est encore à l'écran (voir `saisie.ts`). */
  saisieEnCours: boolean;
  /** Temps écoulé depuis l'ouverture de la page ou le retour sur l'onglet (ms). */
  msDepuisAffichage: number;
}

/** Délai après l'ouverture ou le retour sur l'onglet pendant lequel on recharge sans gêner. */
export const DELAI_RETOUR_MS = 15_000;
/** Pas deux rechargements automatiques en moins de ce délai (évite toute boucle). */
export const DELAI_ENTRE_RECHARGEMENTS_MS = 60_000;
/** Vérification périodique d'une nouvelle version. */
export const INTERVALLE_VERIFICATION_MS = 10 * 60_000;

export function choisirBascule(e: EtatPage): Bascule {
  if (!e.enLigne) return 'attendre';
  if (e.saisieEnCours) return 'proposer';
  // Onglet en arrière-plan, ou l'utilisateur vient d'ouvrir la page ou d'y revenir : le
  // rechargement passe inaperçu. Sinon on ne coupe pas une lecture : bandeau, puis
  // rechargement complet au prochain lien suivi.
  if (!e.visible || e.msDepuisAffichage < DELAI_RETOUR_MS) return 'recharger';
  return 'proposer';
}

/** Version utilisable pour comparer (« dev » et vide ne se comparent pas). */
function significative(v: string | null | undefined): v is string {
  return typeof v === 'string' && v.trim() !== '' && v !== 'dev';
}

/**
 * La page affichée est-elle plus ancienne que la version servie ? Version de l'interface
 * (construction) et version servie (`config.json`, écrite au déploiement) quand les deux sont
 * connues ; sinon le seul signal du service worker (une nouvelle version vient de s'activer).
 */
export function estPerimee(
  versionPage: string | null | undefined,
  versionServie: string | null | undefined,
  signalServiceWorker: boolean,
): boolean {
  if (significative(versionPage) && significative(versionServie))
    return versionPage !== versionServie;
  return signalServiceWorker;
}

/** Un rechargement automatique est-il permis (dernier rechargement assez ancien) ? */
export function peutRecharger(dernier: number | null, maintenant: number): boolean {
  return dernier === null || !(maintenant - dernier < DELAI_ENTRE_RECHARGEMENTS_MS);
}

/**
 * Lien d'Oasis à suivre par un chargement complet quand une nouvelle version attend : même
 * origine, pas l'API ni le client d'affichage, pas de nouvel onglet ni de téléchargement.
 */
export function lienARechargerEnEntier(
  lien: { href: string; target?: string | null; download?: boolean },
  origine: string,
): boolean {
  if (lien.download === true) return false;
  if (
    lien.target !== undefined &&
    lien.target !== null &&
    lien.target !== '' &&
    lien.target !== '_self'
  )
    return false;
  let url: URL;
  try {
    url = new URL(lien.href, origine);
  } catch {
    return false;
  }
  if (url.origin !== origine) return false;
  return !/^\/(api\/|ecran)/.test(url.pathname);
}

/**
 * Échec de chargement d'un morceau de l'interface (import dynamique d'un écran) : après une
 * mise en production, un onglet resté sur l'ancienne version demande des fichiers
 * `/assets/*.js` qui n'existent plus (404). Messages des navigateurs : Chromium (« Failed to
 * fetch dynamically imported module »), Firefox (« error loading dynamically imported
 * module »), Safari (« Importing a module script failed »), préchargement de Vite (« Unable to
 * preload CSS »).
 */
export function estEchecChargementMorceau(erreur: unknown): boolean {
  if (erreur === null || erreur === undefined) return false;
  const objet = typeof erreur === 'object' ? (erreur as { name?: unknown; message?: unknown }) : {};
  if (objet.name === 'ChunkLoadError') return true;
  const message =
    typeof erreur === 'string' ? erreur : typeof objet.message === 'string' ? objet.message : '';
  return /dynamically imported module|importing a module script failed|unable to preload|failed to load module script|loading (css )?chunk [\w-]+ failed/i.test(
    message,
  );
}

/** Fenêtre de la garde : un seul rechargement automatique pour un morceau introuvable. */
export const DELAI_GARDE_MORCEAU_MS = 5 * 60_000;

/**
 * Que faire d'un morceau introuvable ? Recharger une seule fois (la page prend la version
 * servie) ; si un rechargement pour la même raison vient d'avoir lieu (garde en
 * `sessionStorage`), qu'une saisie est en cours ou que le réseau manque : bandeau « Nouvelle
 * version d'Oasis disponible » avec « Recharger », jamais de boucle.
 */
export function reagirEchecMorceau(e: {
  enLigne: boolean;
  saisieEnCours: boolean;
  /** Heure du dernier rechargement automatique pour un morceau introuvable (ou `null`). */
  dernierRechargement: number | null;
  maintenant: number;
}): 'recharger' | 'bandeau' {
  if (!e.enLigne || e.saisieEnCours) return 'bandeau';
  if (
    e.dernierRechargement !== null &&
    e.maintenant - e.dernierRechargement < DELAI_GARDE_MORCEAU_MS
  )
    return 'bandeau';
  return 'recharger';
}
